import * as _ from 'lodash';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { PriceMgr } from './PriceMgr';
import { reduceRightWithParent } from './OrderHelpers';
import { extractValueFromOrderTags } from './OrderTags';
const MY_GENERATION = DomainCoreGeneration.Generation5;
export const applyCombinatorDiscounts = (topLevelOrderLines, rulesToApply) => {
    const productIdsWithHigherQuantity = [];
    const ungroupedLines = topLevelOrderLines.reduce((acc, line) => {
        switch (line.type) {
            case 'Product': {
                if (line.quantity) {
                    const xs = [];
                    if (line.quantity > 1) {
                        productIdsWithHigherQuantity.push(line.productId);
                    }
                    for (let i = 0; i < line.quantity; i++) {
                        xs.push(Object.assign(Object.assign({}, line), { quantity: 1 }));
                    }
                    return [...acc, ...xs];
                }
                else {
                    return [...acc, line];
                }
            }
            default: {
                return [...acc, line];
            }
        }
    }, []);
    const createCombinatorLine = (newCombinedPrice) => (xs) => {
        return xs.reduce((acc, curr) => {
            accumulatePrices([curr]);
            const additionalExpenses = reduceRightWithParent([curr], (acc, c, parr) => {
                if (c.type === 'Aggregation') {
                    if (c.options.includedInMenuPrice) {
                        const u = c.totalPriceAccum - c.options.includedInMenuPrice;
                        return acc + u;
                    }
                    else if ('totalPrice' in parr &&
                        'totalPriceAccum' in parr &&
                        (parr.totalPriceAccum == parr.totalPrice || parr.totalPrice === 0)) {
                        return acc + (c.totalPriceAccum || 0);
                    }
                    return acc;
                }
                return acc;
            }, 0, null);
            return Object.assign(Object.assign({}, acc), { name: acc.name === '' ? curr.name : `${acc.name} & ${curr.name}`, subLines: [...acc.subLines, curr], originalTotalPriceAccum: acc.originalTotalPriceAccum + (curr.type === 'Product' ? curr.totalPriceAccum : 0), totalPriceAccum: acc.totalPriceAccum + additionalExpenses, productIds: [...acc.productIds, curr.type === 'Product' ? curr.productId : ''] });
        }, {
            name: '',
            options: {},
            subLines: [],
            type: 'CombinatorLine',
            generation: xs[0].generation,
            totalPriceAccum: newCombinedPrice,
            totalPrice: newCombinedPrice,
            originalTotalPriceAccum: 0,
            productIds: [],
            quantity: 1,
        });
    };
    const application = rulesToApply.reduce((acc, rule) => {
        const { matched, unmatched } = acc.topLevelOrderLines.reduce((acc, line) => {
            if (line.subLines && line.subLines.length > 0) {
                const secondLevel = applyCombinatorDiscounts(line.subLines, rulesToApply);
                if (line.type !== 'CombinatorLine' ||
                    (secondLevel[0].length === 0 && secondLevel[1].every((d) => d.type !== 'CombinatorLine'))) {
                    line.subLines = [].concat(...secondLevel);
                }
            }
            if (line.type === 'Product') {
                if (rule.productPattern.includes(line.productId)) {
                    return Object.assign(Object.assign({}, acc), { matched: [...acc.matched, line] });
                }
            }
            return Object.assign(Object.assign({}, acc), { unmatched: [...acc.unmatched, line] });
        }, { matched: [], unmatched: [] });
        const reminderCount = matched.length % rule.options.applyQuantity;
        const splitIndex = matched.length - reminderCount;
        const [groups, remindingLines] = [matched.slice(0, splitIndex), matched.slice(splitIndex, matched.length)];
        const newCombinedLines = _.chunk(groups, rule.options.applyQuantity).map(createCombinatorLine(rule.options.newCombinedPrice));
        return {
            topLevelOrderLines: remindingLines.concat(unmatched).reduce((acc, line) => {
                switch (line.type) {
                    case 'Product': {
                        const olAlreadyInAcc = acc.find((ol) => ol.productId === line.productId && productIdsWithHigherQuantity.includes(ol.productId));
                        if (olAlreadyInAcc) {
                            const differentRuleExist = rulesToApply.find((secondRule) => secondRule.discountId !== rule.discountId && secondRule.productPattern.includes(line.productId));
                            if (!differentRuleExist) {
                                return acc.map((ol) => {
                                    if (ol.productId === line.productId) {
                                        return Object.assign(Object.assign({}, ol), { quantity: ol.quantity + 1 });
                                    }
                                    return ol;
                                });
                            }
                        }
                        return [...acc, line];
                    }
                    default: {
                        return [...acc, line];
                    }
                }
            }, []),
            combinedLines: [...acc.combinedLines, ...newCombinedLines],
        };
    }, { topLevelOrderLines: ungroupedLines, combinedLines: [] });
    return [application.combinedLines, application.topLevelOrderLines];
};
const isVoucherValid = (discount, options) => _.intersection(_.get(discount, 'voucherPattern'), _.get(options, 'vouchers')).length > 0;
export const shouldApplyDiscount = (discount, options) => {
    const voucherValid = isVoucherValid(discount, options);
    const voucherPattern = (discount === null || discount === void 0 ? void 0 : discount.voucherPattern) || [];
    return voucherPattern.length === 0 || voucherValid;
};
export const findBestApplicableDiscount = (applicableDiscounts, options, tempComputedPrice) => {
    if (!applicableDiscounts || !applicableDiscounts.length)
        return null;
    const filteredApplicableDiscounts = applicableDiscounts.filter((discount) => isVoucherValid(discount, options));
    if (!filteredApplicableDiscounts.length) {
        return null;
    }
    return filteredApplicableDiscounts
        .map((discount) => {
        return Object.assign(Object.assign({}, discount), { discountedPrice: PriceMgr.compute(tempComputedPrice) });
    })
        .reduce((prev, curr) => {
        return 'discountedPrice' in prev && prev.discountedPrice > curr.discountedPrice ? prev : curr;
    }, {});
};
export const applyDiscountsForOrderLines = (topLevelOrderLines, options = {}, orderTags) => {
    const newOrderLines = [];
    const voucherIdAndName = Array.isArray(orderTags) && extractValueFromOrderTags('Voucher', orderTags);
    let usesPerTransactionToInt = 1;
    let voucherOnlinePosProductId = null;
    if (voucherIdAndName) {
        const voucherIdAndNameSplit = voucherIdAndName.split(',').map((e) => e.trim());
        usesPerTransactionToInt = parseInt(voucherIdAndNameSplit[3]);
        voucherOnlinePosProductId = parseInt(voucherIdAndNameSplit[4]);
    }
    let availableUsesPerTransaction = usesPerTransactionToInt;
    const recurse = (line, _newOrderLines, level = 0, sentParent) => {
        const discount = 'discount' in line ? line.discount : undefined;
        const applicableDiscounts = 'applicableDiscounts' in line ? line.applicableDiscounts : undefined;
        const shouldApply = shouldApplyDiscount(discount, options);
        let newLine = Object.assign({}, line);
        newLine.subLines = [];
        const parent = level === 0 ? newLine : sentParent;
        const quantity = 'quantity' in line ? line.quantity : 1;
        const computedPrices = Object.assign(Object.assign(Object.assign(Object.assign({}, line), { discount: shouldApply && (usesPerTransactionToInt === 0 || availableUsesPerTransaction > 0) ? discount : null, discountQuantity: 0 }), ('price' in line ? { price: line['price'] } : {})), ('quantity' in line ? { quantity: line['quantity'] } : {}));
        if (line.subLines) {
            line.subLines.forEach((subLine) => {
                recurse(subLine, newLine.subLines, level + 1, parent);
            });
        }
        if (voucherIdAndName && (usesPerTransactionToInt === 0 || availableUsesPerTransaction > 0)) {
            const bestApplicableDiscount = findBestApplicableDiscount(applicableDiscounts, options, computedPrices);
            if (bestApplicableDiscount) {
                computedPrices.discount = bestApplicableDiscount;
                computedPrices.discountQuantity = quantity;
                const [voucherCode, voucherId, voucherName] = voucherIdAndName.split(',').map((e) => e.trim());
                const voucherSubline = {
                    name: 'VOUCHER',
                    subLines: [],
                    type: 'Voucher',
                    tags: [`Voucher.${voucherCode}.${voucherId}.${voucherName}`],
                    generation: MY_GENERATION,
                    values: [
                        {
                            type: 'Voucher',
                            voucherId,
                            voucherName,
                            voucherCode,
                            usesPerTransaction: usesPerTransactionToInt,
                            onlinePosProductId: voucherOnlinePosProductId,
                        },
                    ],
                };
                if (usesPerTransactionToInt !== 0) {
                    if (quantity > availableUsesPerTransaction) {
                        computedPrices.discountQuantity = availableUsesPerTransaction;
                    }
                    availableUsesPerTransaction -= quantity;
                }
                newLine.subLines.push(voucherSubline);
            }
        }
        newLine = Object.assign(Object.assign({}, newLine), PriceMgr.compute(computedPrices));
        _newOrderLines.push(newLine);
    };
    topLevelOrderLines.forEach((line) => {
        recurse(line, newOrderLines);
    });
    return newOrderLines;
};
export const accumulatePrices = (topLevelOrderLines) => {
    const accumulatePricesRecurse = function (line) {
        let totalPriceAccumFromSublinesSummed = 0;
        let totalPriceBeforeAccumFromSublinesSummed = 0;
        let didAnyChildNodeHaveTotalPriceAccum = false;
        let didAnyChildNodeHaveTotalPriceBeforeAccum = false;
        if (line.subLines && line.subLines.length > 0 && line.type !== 'CombinatorLine') {
            line.subLines.forEach((subLine) => {
                const { totalPriceAccum, totalPriceBeforeAccum } = accumulatePricesRecurse(subLine);
                if (Number.isFinite(totalPriceAccum)) {
                    totalPriceAccumFromSublinesSummed += totalPriceAccum;
                    didAnyChildNodeHaveTotalPriceAccum = true;
                }
                totalPriceBeforeAccumFromSublinesSummed += totalPriceBeforeAccum
                    ? totalPriceBeforeAccum
                    : totalPriceAccum
                        ? totalPriceAccum
                        : 0;
                if (Number.isFinite(totalPriceBeforeAccum)) {
                    didAnyChildNodeHaveTotalPriceBeforeAccum = true;
                }
            });
        }
        const result = {};
        const myQuantity = 'quantity' in line ? line.quantity : 1;
        if (line.type !== 'CombinatorLine') {
            const totalPrice = line.type === 'Product' || line.type === 'Discount' ? line.totalPrice : undefined;
            if (didAnyChildNodeHaveTotalPriceAccum || Number.isFinite(totalPrice)) {
                result.totalPriceAccum = myQuantity * totalPriceAccumFromSublinesSummed + (totalPrice ? totalPrice : 0);
            }
            if (didAnyChildNodeHaveTotalPriceBeforeAccum || 'totalPriceBefore' in line) {
                const effectiveTotalPriceBefore = 'totalPriceBefore' in line
                    ? line.totalPriceBefore
                    : 'totalPrice' in line && line.totalPrice
                        ? line.totalPrice
                        : 0;
                result.totalPriceBeforeAccum = myQuantity * totalPriceBeforeAccumFromSublinesSummed + effectiveTotalPriceBefore;
            }
        }
        else {
            if (line.type === 'CombinatorLine') {
                line.subLines.forEach((subLine) => {
                    if (!isMenu(subLine)) {
                        const aggregations = subLine.subLines.filter((item) => {
                            return item.type === 'Aggregation' && item.totalPriceAccum > 0;
                        });
                        if (aggregations && aggregations.length > 0) {
                            aggregations.forEach((aggregation) => {
                                if (aggregation && aggregation.totalPriceAccum) {
                                    totalPriceAccumFromSublinesSummed += aggregation.totalPriceAccum;
                                }
                            });
                        }
                    }
                });
            }
            result.totalPriceAccum = myQuantity * totalPriceAccumFromSublinesSummed + line.totalPriceAccum;
        }
        Object.assign(line, result);
        return result;
    };
    topLevelOrderLines.forEach(accumulatePricesRecurse);
};
export const aggregateQuantityTopDown = (rootOrderLines) => {
    function recurse(lines, ancestorQuantity = undefined) {
        lines.forEach((line) => {
            const hasQuantity = 'quantity' in line && line.quantity !== undefined;
            if (hasQuantity) {
                if (ancestorQuantity !== undefined) {
                    line.aggregatedQuantity = ancestorQuantity;
                }
                else {
                    line.aggregatedQuantity = 1;
                }
            }
            function getNewAncestorQuantity() {
                if (ancestorQuantity === undefined && !hasQuantity) {
                    return undefined;
                }
                else {
                    const q1 = ancestorQuantity !== undefined ? ancestorQuantity : 1;
                    const q2 = 'quantity' in line ? line.quantity : 1;
                    return q1 * q2;
                }
            }
            if (line.subLines && line.subLines.length) {
                const newAncestorQuantity = getNewAncestorQuantity();
                recurse(line.subLines, newAncestorQuantity);
            }
        });
    }
    recurse(rootOrderLines);
};
const isMenu = (subLine) => {
    if (!subLine.subLines || subLine.subLines.length === 0) {
        return false;
    }
    const menuItem = subLine.subLines.find((item) => {
        return item.name === 'Menu Size';
    });
    return !!menuItem;
};
export function fixPricesForProductsWithFreeAdditionsG5(lines) {
    function fixPricesRecurse(freeQuantity, lines) {
        if (!lines || lines.length === 0) {
            return;
        }
        if (freeQuantity && freeQuantity > 0) {
            lines.sort((a, b) => a.price - b.price);
            let sumQ = 0;
            let i = 0;
            while (sumQ < freeQuantity && i < lines.length) {
                const q = lines[i].quantity;
                const remaining = freeQuantity - sumQ;
                const freeQ = Math.min(q, remaining);
                const newTotalPrice = lines[i].totalPrice - freeQ * lines[i].price;
                if (newTotalPrice === 0) {
                    delete lines[i].totalPrice;
                }
                else {
                    lines[i].totalPrice = newTotalPrice;
                }
                if (!lines[i].tags2) {
                    lines[i].tags2 = {};
                }
                lines[i].tags2.freeQuantity = true;
                i++;
                sumQ += freeQ;
            }
        }
        lines.forEach((line) => {
            if (line.subLines) {
                const freeQuantityOfParent = !line.options ? 0 : line.options.freeQuantity || 0;
                fixPricesRecurse(freeQuantityOfParent, line.subLines);
            }
        });
    }
    fixPricesRecurse(0, lines);
}
